import {
  ApolloClient,
  ApolloLink,
  createHttpLink,
  from,
  InMemoryCache,
  ServerError,
} from '@apollo/client'
import { setContext } from '@apollo/client/link/context'
import { OktaAuth, toRelativeUrl } from '@okta/okta-auth-js'
import DebounceLink from 'apollo-link-debounce'
import { logUsage } from '../services/usageLog'
import { onError } from '@apollo/client/link/error'

const httpLink = createHttpLink({
  uri: process.env.REACT_APP_SHIPMENT_TRACKING_GRAPHQL_URL,
})

const DEBOUNCE_TIMEOUT = 200

export function createApolloClient(oktaAuth: OktaAuth) {
  const logUsageLink = new ApolloLink((operation, forward) => {
    const action = operation.variables.action ? operation.variables.action : operation.operationName
    const searchInput = { ...operation.variables }
    Object.keys(searchInput).forEach(
      (k) =>
        (searchInput[k] === undefined || ['size', 'action'].includes(k)) && delete searchInput[k]
    )
    logUsage(operation.getContext().page, action, { filters: searchInput })
    return forward(operation)
  })

  const authLink = setContext((_, { headers }) => {
    const token = oktaAuth.getAccessToken()
    return {
      headers: {
        ...headers,
        authorization: token || '',
      },
    }
  })

  const errorLink = onError(({ networkError }) => {
    if ((networkError as ServerError)?.statusCode === 401) {
      const originalUri = toRelativeUrl(window.location.href, window.location.origin)
      oktaAuth.setOriginalUri(originalUri)
      oktaAuth.signInWithRedirect()
    }
  })

  return new ApolloClient({
    link: from([new DebounceLink(DEBOUNCE_TIMEOUT), logUsageLink, authLink, errorLink, httpLink]),
    cache: new InMemoryCache({
      resultCaching: false,
    }),
    defaultOptions: {
      query: {
        fetchPolicy: 'no-cache',
      },
    },
    assumeImmutableResults: true,
    connectToDevTools: process.env.NODE_ENV === 'development',
  })
}
